import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconId,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import DatePickerField from '@/components/common/DatePickerField';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import MissingFieldsDialog from '../shared/MissingFieldsDialog';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import EmbassyBirthPreviewFrame from './EmbassyBirthPreviewFrame';
import EmbassyBirthFormInfoBox from './EmbassyBirthFormInfoBox';
import {
    DEFAULT_NATIONALITY,
    GENDER_KEYS,
    INITIAL_EMBASSY_BIRTH_FORM,
    RELATION_KEYS,
    displayField,
    formatDateField,
    getEmbassyBirthFormSnapshot,
    getEmptyEmbassyBirthFields,
    isEmbassyBirthFormComplete,
    parseDateField,
    saveEmbassyBirthFormSnapshot,
    splitDate,
} from './embassyBirthFormEngine';
import { downloadEmbassyBirthPdf, printEmbassyBirthForm } from './generateEmbassyBirthPdf';

const SLUG = 'general/embassy-birth-registration-form';

const inputBaseClass = 'w-full rounded-xl border bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:ring-2 focus:ring-purple-400/30 dark:bg-zinc-900 dark:text-zinc-100';
const inputOkClass = 'border-[#ECE8E4] focus:border-purple-400 dark:border-zinc-700';

/** Calendar range: any living person's birth date, and no future days. */
const TODAY = new Date();
const BIRTH_FROM_YEAR = 1900;

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ number, title, children }) {
    return (
        <div className="rounded-2xl border border-purple-200 bg-white px-4 py-4 dark:border-purple-900/40 dark:bg-zinc-900">
            <h3 className="mb-3.5 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">
                {number}. {title}
            </h3>
            {children}
        </div>
    );
}

/** One of the form's tick boxes, offered as a choice of pills. */
function ChoiceGroup({ name, options, value, onChange }) {
    return (
        <div role="radiogroup" className="flex flex-wrap gap-2">
            {options.map(({ key, label }) => {
                const active = value === key;
                return (
                    <label
                        key={key}
                        className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-[13px] transition-colors ${active
                            ? 'border-purple-400 bg-purple-50 text-purple-800 dark:border-purple-600 dark:bg-purple-900/30 dark:text-purple-200'
                            : 'border-[#ECE8E4] text-[#3D3833] dark:border-zinc-700 dark:text-zinc-200'}`}
                    >
                        <input
                            type="radio"
                            name={name}
                            checked={active}
                            onChange={() => onChange(key)}
                            className="h-4 w-4 accent-purple-600"
                        />
                        {label}
                    </label>
                );
            })}
            {value ? (
                <button
                    type="button"
                    onClick={() => onChange('')}
                    className="rounded-xl px-2 py-2 text-[12px] text-[#8A8178] hover:text-[#3D3833] dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                    ✕
                </button>
            ) : null}
        </div>
    );
}

export default function EmbassyBirthFormGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const pageZoom = usePageZoom();
    const snapshotTimerRef = useRef(null);

    const [form, setForm] = useState(INITIAL_EMBASSY_BIRTH_FORM);
    const [downloading, setDownloading] = useState(false);
    const [confirmingDownload, setConfirmingDownload] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [hasSavedSnapshot, setHasSavedSnapshot] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHasSavedSnapshot(!!getEmbassyBirthFormSnapshot());
        }
    }, []);

    const update = useCallback((key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            window.clearTimeout(snapshotTimerRef.current);
            if (displayField(next.registrantName)) {
                snapshotTimerRef.current = window.setTimeout(() => {
                    saveEmbassyBirthFormSnapshot(next);
                    setHasSavedSnapshot(true);
                }, 400);
            }
            return next;
        });
    }, []);

    /**
     * Remember what was typed, then export — whatever state the form is in.
     * Missing fields do not block the download: printing the form half-filled to
     * finish by hand at the mission is a normal way to use it. Downloading asks
     * for a confirmation that names what is still empty; printing does not.
     */
    const prepareForExport = () => {
        saveEmbassyBirthFormSnapshot(form);
        setHasSavedSnapshot(true);
    };

    const emptyFields = useMemo(() => getEmptyEmbassyBirthFields(form, t), [form, t]);

    const runDownloadPdf = async () => {
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('embassyBirthForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setDownloading(true);
        try {
            recordToolUsage(SLUG, { category: 'general' });
            const base = form.registrantName.trim().replace(/\s+/g, '-').slice(0, 40) || 'application';
            await downloadEmbassyBirthPdf(previewRef.current, `birth-registration-${base}.pdf`);
            notifications.show({
                title: t('common.success'),
                message: t('embassyBirthForm.downloadSuccess'),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('embassyBirthForm.errors.pdfFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = () => {
        if (emptyFields.length) {
            setConfirmingDownload(true);
            return;
        }
        runDownloadPdf();
    };

    const confirmDownloadPdf = () => {
        setConfirmingDownload(false);
        runDownloadPdf();
    };

    const handlePrint = async () => {
        recordToolUsage(SLUG, { category: 'general' });
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('embassyBirthForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setPrinting(true);
        try {
            await printEmbassyBirthForm(previewRef.current, t('embassyBirthForm.errors.popupBlocked'));
            notifications.show({
                title: t('embassyBirthForm.printTitle'),
                message: t('embassyBirthForm.printSuccess'),
                color: 'blue',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('embassyBirthForm.errors.printFailed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    const handleClear = () => {
        setForm(INITIAL_EMBASSY_BIRTH_FORM);
        notifications.show({
            title: t('embassyBirthForm.clearedTitle'),
            message: t('embassyBirthForm.clearedMessage'),
            color: 'gray',
        });
    };

    const handleRestoreSnapshot = () => {
        const snapshot = getEmbassyBirthFormSnapshot();
        if (!snapshot) {
            notifications.show({
                title: t('embassyBirthForm.noSnapshotTitle'),
                message: t('embassyBirthForm.noSnapshotMessage'),
                color: 'gray',
            });
            return;
        }

        setForm(snapshot);
        notifications.show({
            title: t('embassyBirthForm.restoredTitle'),
            message: t('embassyBirthForm.restoredMessage'),
            color: 'green',
        });
    };

    // The nationalities start filled, so they do not count as the user typing.
    const hasAnyInput = useMemo(
        () => Object.entries(form).some(([key, value]) => (
            displayField(value) && !(key.endsWith('Nationality') && value === DEFAULT_NATIONALITY)
        )),
        [form],
    );

    const isComplete = useMemo(() => isEmbassyBirthFormComplete(form, t), [form, t]);

    const status = deriveToolStatus({
        hasInput: hasAnyInput,
        canRun: isComplete,
        hasResult: isComplete,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('embassyBirthForm.statusReady'),
        success: t('embassyBirthForm.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`embassyBirthForm.howTo.step${i}`)),
        [t],
    );

    /** The birth date as the "কথায়" row will print it, so the applicant can check it. */
    const birthWords = splitDate(parseDateField(form.birthDate));

    const textField = (key, extra = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`embassyBirthForm.fields.${key}`)}</FormLabel>
            <input
                id={key}
                name={key}
                value={form[key]}
                onChange={(e) => update(key, e.target.value)}
                placeholder={t(`embassyBirthForm.placeholders.${key}`)}
                className={`${inputBaseClass} ${inputOkClass}`}
                {...extra}
            />
        </div>
    );

    /** Dates are picked, so they always come out দিন-মাস-বছর in Bengali digits. */
    const dateField = (key, { fromYear = 1950 } = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`embassyBirthForm.fields.${key}`)}</FormLabel>
            <DatePickerField
                value={parseDateField(form[key])}
                onChange={(date) => update(key, formatDateField(date))}
                formatValue={formatDateField}
                placeholder={t(`embassyBirthForm.placeholders.${key}`)}
                triggerClassName={`${inputBaseClass} ${inputOkClass}`}
                fromYear={fromYear}
                toYear={TODAY.getFullYear()}
                disabled={{ after: TODAY }}
                clearable
            />
        </div>
    );

    return (
        <>
        <MissingFieldsDialog
            open={confirmingDownload}
            fields={emptyFields}
            loading={downloading}
            onCancel={() => setConfirmingDownload(false)}
            onConfirm={confirmDownloadPdf}
        />
        <ToolPageLayout
            slug={SLUG}
            subtitleKey="embassyBirthForm.subtitle"
            variant="wideControls"
            railWidthClass="lg:w-[calc(70%_-_9px)]"
            status={status}
            statusText={statusText}
            controlsTitle={t('embassyBirthForm.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasAnyInput ? handleClear : undefined}
            resetLabel={t('embassyBirthForm.clearAll')}
            primaryAction={{
                label: t('embassyBirthForm.downloadPdf'),
                onClick: handleDownloadPdf,
                disabled: printing,
                loading: downloading,
                icon: IconDownload,
            }}
            secondaryAction={[
                hasSavedSnapshot ? {
                    key: 'restore',
                    label: t('embassyBirthForm.restoreLast'),
                    onClick: handleRestoreSnapshot,
                    disabled: downloading || printing,
                    icon: IconRefresh,
                } : null,
                {
                    key: 'print',
                    label: printing ? t('toolPage.processing') : t('embassyBirthForm.print'),
                    onClick: handlePrint,
                    disabled: downloading || printing,
                    icon: IconPrinter,
                },
            ]}
            controls={(
                <div className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]">
                    <FormSection
                        number={t('embassyBirthForm.sections.registrant.number')}
                        title={t('embassyBirthForm.sections.registrant.title')}
                    >
                        <div className="space-y-3">
                            {textField('registrantName', { autoComplete: 'off' })}
                            {dateField('birthDate', { fromYear: BIRTH_FROM_YEAR })}
                            {birthWords.day ? (
                                <p className="rounded-lg bg-purple-50 px-3 py-2 text-[12.5px] text-purple-800 dark:bg-purple-900/20 dark:text-purple-200">
                                    {t('embassyBirthForm.birthWords', {
                                        day: birthWords.dayWords,
                                        month: birthWords.monthWords,
                                        year: birthWords.yearWords,
                                    })}
                                </p>
                            ) : null}
                            <div>
                                <FormLabel>{t('embassyBirthForm.fields.gender')}</FormLabel>
                                <ChoiceGroup
                                    name="gender"
                                    value={form.gender}
                                    onChange={(v) => update('gender', v)}
                                    options={GENDER_KEYS.map((key) => ({ key, label: t(`embassyBirthForm.genders.${key}`) }))}
                                />
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('birthPlace')}
                                {textField('passportNo', { autoComplete: 'off' })}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('embassyBirthForm.sections.parents.number')}
                        title={t('embassyBirthForm.sections.parents.title')}
                    >
                        <div className="space-y-3">
                            {textField('fatherName')}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('fatherNationality')}
                                {textField('fatherPassport', { autoComplete: 'off' })}
                            </div>
                            {textField('motherName')}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('motherNationality')}
                                {textField('motherPassport', { autoComplete: 'off' })}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('embassyBirthForm.sections.address.number')}
                        title={t('embassyBirthForm.sections.address.title')}
                    >
                        <div className="space-y-3">
                            {textField('permanentAddress')}
                            {textField('presentAddress', { autoComplete: 'street-address' })}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('embassyBirthForm.sections.declaration.number')}
                        title={t('embassyBirthForm.sections.declaration.title')}
                    >
                        <div className="space-y-3">
                            {textField('declarantName', { autoComplete: 'name' })}
                            <div>
                                <FormLabel>{t('embassyBirthForm.fields.declarantRelation')}</FormLabel>
                                <ChoiceGroup
                                    name="declarantRelation"
                                    value={form.declarantRelation}
                                    onChange={(v) => update('declarantRelation', v)}
                                    options={RELATION_KEYS.map((key) => ({ key, label: t(`embassyBirthForm.relations.${key}`) }))}
                                />
                            </div>
                            {form.declarantRelation === 'other' ? textField('declarantRelationOther') : null}
                            {dateField('applicationDate')}
                        </div>
                    </FormSection>
                </div>
            )}
            preview={(
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('embassyBirthForm.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('embassyBirthForm.previewEmpty')}
                        idleHint={t('embassyBirthForm.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconId size={14} stroke={1.8} />
                                {t('embassyBirthForm.livePreview')}
                            </div>
                            <EmbassyBirthPreviewFrame form={form} previewRef={previewRef} zoom={pageZoom.zoom} />
                        </div>
                    </ToolPreviewPanel>
                </div>
            )}
            belowWorkArea={<EmbassyBirthFormInfoBox />}
        />
        </>
    );
}
